import {ICoordinates, IobrDataServerForRadarClusters} from "../../../types";
import {signal_trend} from "../../../pages/Fintech/FintechDashboard.type";
import {xScale} from "../../../mocks/projectsMock";
import {avoidCollisions} from "./collisionAvoidance";

const getMaxAllowedRadius = (
  angleOffset: number,
  availableAngle: number,
  maxRadius: number,
  minRadius: number
): number => {
  const normalizedOffset = Math.abs(angleOffset) / (availableAngle / 2);
  const reductionFactor = 1 - normalizedOffset * 0.2;
  const effectiveMaxRadius = minRadius + (maxRadius - minRadius) * reductionFactor;
  return Math.max(effectiveMaxRadius, minRadius);
};

interface CalculateClusterCoordinatesParams {
  cluster: IobrDataServerForRadarClusters;
  count: number;
  index: number;
  maxRadius: number;
  minRadius: number;
  stepHeight: number;
  segmentCenterAngle: number;
  availableAngle: number;
  typeRadar: string;
  isWeak_signal?: boolean;
  prevSignalTrend: signal_trend | undefined;
  coordinates: ICoordinates[];
  updateCoordinates: (
    id: number | string,
    xStart: number,
    yStart: number,
    xEnd: number,
    yEnd: number
  ) => void;
  isFiltersNoActive: boolean;
  maturity: string;
}

const calculateClusterCoordinates = ({
  cluster,
  count,
  index,
  maxRadius,
  minRadius,
  stepHeight,
  segmentCenterAngle,
  availableAngle,
  typeRadar,
  isWeak_signal,
  prevSignalTrend,
  coordinates,
  updateCoordinates,
  isFiltersNoActive,
  maturity,
}: CalculateClusterCoordinatesParams) => {
  let currentTrend: number | undefined;
  let dotStart: number | undefined;
  let currentHeight: number | undefined;
  let heightStart: number | undefined;
  let heightEnd: number | undefined;

  dotStart = cluster.trend_score;

  if (count <= 1) {
    currentHeight = (maxRadius + minRadius) / 2;
  } else {
    if (typeRadar === "fintech" && !isWeak_signal) {
      currentHeight = maxRadius - (currentTrend || 0) * xScale;
      heightStart = maxRadius - (dotStart || 0) * xScale;
    } else if (typeRadar === "molecula" || typeRadar === "moleculeTwo") {
      currentHeight = (maxRadius + minRadius) / 2 - 5;
    } else {
      const marginPercent = 0.10;
      const effectiveMin = minRadius + (maxRadius - minRadius) * marginPercent;
      const effectiveMax = maxRadius - (maxRadius - minRadius) * marginPercent;
      const effectiveRange = effectiveMax - effectiveMin;

      // Layer distribution: 3, 3, 4, 5, 6, 7
      const getMaxPointsForLayer = (layer: number) => {
        if (layer <= 2) return 3;
        return Math.min(4 + (layer - 2), 7);
      };

      let layerIndex = 0;
      let pointsDistributed = 0;
      let remainingPoints = count;

      while (remainingPoints > 0) {
        const maxPointsInThisLayer = getMaxPointsForLayer(layerIndex);
        const pointsInThisLayer = Math.min(maxPointsInThisLayer, remainingPoints);

        if (index < pointsDistributed + pointsInThisLayer) break;

        pointsDistributed += pointsInThisLayer;
        remainingPoints -= pointsInThisLayer;
        layerIndex++;
      }

      let totalLayers = 0;
      let remainingPointsTotal = count;
      while (remainingPointsTotal > 0) {
        const maxPointsInLayer = getMaxPointsForLayer(totalLayers);
        const pointsInLayer = Math.min(maxPointsInLayer, remainingPointsTotal);
        remainingPointsTotal -= pointsInLayer;
        totalLayers++;
      }

      const layerStep = effectiveRange / Math.max(totalLayers - 1, 1);
      currentHeight = effectiveMin + layerStep * layerIndex;

      const idHash = typeof cluster.id === 'number' ? cluster.id :
        String(cluster.id).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const seed = (index * 17 + idHash * 23) % 100;
      const variation = (seed / 100 - 0.5) * layerStep * 0.3;
      currentHeight += variation;

      currentHeight = Math.max(effectiveMin, Math.min(effectiveMax, currentHeight));
    }
  }

  if (isNaN(currentHeight)) {
    console.error(`NaN height calculated for cluster ${cluster.id}`);
    return null;
  }

  let angleOffset = 0;

  if (count > 1) {
    if (typeRadar === "molecula" || typeRadar === "moleculeTwo") {
      const availableAngleNew =
        typeRadar === "moleculeTwo" ? 360 : availableAngle;
      const angleBetweenRays = availableAngleNew / count;

      angleOffset = -availableAngleNew / 2 + (index + 0.5) * angleBetweenRays;
    } else {
      const angleMarginPercent = 0.05;
      const effectiveAngle = availableAngle * (1 - 2 * angleMarginPercent);

      // Layer distribution: 3, 3, 4, 5, 6, 7
      const getMaxPointsForLayer = (layer: number) => {
        if (layer <= 2) return 3;
        return Math.min(4 + (layer - 2), 7);
      };

      let layerIndex = 0;
      let pointsDistributed = 0;
      let remainingPoints = count;
      let pointsInCurrentLayer = 0;

      while (remainingPoints > 0) {
        const maxPointsInThisLayer = getMaxPointsForLayer(layerIndex);
        pointsInCurrentLayer = Math.min(maxPointsInThisLayer, remainingPoints);

        if (index < pointsDistributed + pointsInCurrentLayer) break;

        pointsDistributed += pointsInCurrentLayer;
        remainingPoints -= pointsInCurrentLayer;
        layerIndex++;
      }

      const positionInLayer = index - pointsDistributed;
      const angularStep = pointsInCurrentLayer > 1 ? effectiveAngle / (pointsInCurrentLayer - 1) : 0;
      angleOffset = -effectiveAngle / 2 + positionInLayer * angularStep;

      const idHash = typeof cluster.id === 'number' ? cluster.id :
        String(cluster.id).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const seed = (index * 13 + idHash * 29) % 100;
      const variation = (seed / 100 - 0.5) * (angularStep || effectiveAngle * 0.2) * 0.3;
      angleOffset += variation;
    }
  }

  if (typeRadar !== "molecula" && typeRadar !== "moleculeTwo" && typeRadar !== "fintech") {
    const maxAllowedRadius = getMaxAllowedRadius(angleOffset, availableAngle, maxRadius, minRadius);
    currentHeight = Math.min(currentHeight, maxAllowedRadius);
  }

  const rayAngle = segmentCenterAngle + angleOffset;

  let x = Math.cos((rayAngle * Math.PI) / 180) * 105;
  let y = Math.sin((rayAngle * Math.PI) / 180) * 105;

  let xMoleculaTwo =
    Math.cos((rayAngle * Math.PI) / 180) * (currentHeight - 10);
  let yMoleculaTwo =
    Math.sin((rayAngle * Math.PI) / 180) * (currentHeight - 10);

  let xStart =
    Math.cos((rayAngle * Math.PI) / 180) *
    (heightStart ? heightStart : currentHeight);
  let yStart =
    Math.sin((rayAngle * Math.PI) / 180) *
    (heightStart ? heightStart : currentHeight);

  let xEnd =
    Math.cos((rayAngle * Math.PI) / 180) *
    (heightEnd ? heightEnd : currentHeight);
  let yEnd =
    Math.sin((rayAngle * Math.PI) / 180) *
    (heightEnd ? heightEnd : currentHeight);

  const existingCoord = coordinates.find((coord) => coord?.id === cluster.id);
  if (!existingCoord) {
    // Clamp to segment bounds before collision resolution
    if (typeRadar !== "molecula" && typeRadar !== "moleculeTwo" && typeRadar !== "fintech") {
      let adjustedRadius = Math.sqrt(xStart * xStart + yStart * yStart);
      const adjustedAngle = Math.atan2(yStart, xStart) * 180 / Math.PI;
      let adjustedAngleOffset = adjustedAngle - segmentCenterAngle;

      // Normalize to [-180, 180]
      while (adjustedAngleOffset > 180) adjustedAngleOffset -= 360;
      while (adjustedAngleOffset < -180) adjustedAngleOffset += 360;

      const maxAngleOffset = availableAngle / 2 * 0.90;
      if (Math.abs(adjustedAngleOffset) > maxAngleOffset) {
        const clampedAngleOffset = Math.sign(adjustedAngleOffset) * maxAngleOffset;
        const clampedAngle = segmentCenterAngle + clampedAngleOffset;
        const clampedAngleRad = clampedAngle * Math.PI / 180;

        xStart = adjustedRadius * Math.cos(clampedAngleRad);
        yStart = adjustedRadius * Math.sin(clampedAngleRad);
        adjustedAngleOffset = clampedAngleOffset;
      }

      const maxAllowedRadius = getMaxAllowedRadius(adjustedAngleOffset, availableAngle, maxRadius, minRadius);
      adjustedRadius = Math.sqrt(xStart * xStart + yStart * yStart);

      if (adjustedRadius > maxAllowedRadius) {
        const scale = maxAllowedRadius / adjustedRadius;
        xStart = xStart * scale;
        yStart = yStart * scale;
      }

      if (adjustedRadius < minRadius) {
        const scale = minRadius / adjustedRadius;
        xStart = xStart * scale;
        yStart = yStart * scale;
      }
    }

    // Iteratively apply collision avoidance and re-clamp bounds
    const maxIterations = 5;
    for (let iter = 0; iter < maxIterations; iter++) {
      const adjustedPosition = avoidCollisions(
        xStart,
        yStart,
        coordinates,
        cluster.id,
        "combined"
      );

      xStart = adjustedPosition.x;
      yStart = adjustedPosition.y;

      if (typeRadar !== "molecula" && typeRadar !== "moleculeTwo" && typeRadar !== "fintech") {
        let currentRadius = Math.sqrt(xStart * xStart + yStart * yStart);
        const currentAngle = Math.atan2(yStart, xStart) * 180 / Math.PI;
        let currentAngleOffset = currentAngle - segmentCenterAngle;

        while (currentAngleOffset > 180) currentAngleOffset -= 360;
        while (currentAngleOffset < -180) currentAngleOffset += 360;

        const maxAngleOffset = availableAngle / 2 * 0.90;
        if (Math.abs(currentAngleOffset) > maxAngleOffset) {
          const clampedAngleOffset = Math.sign(currentAngleOffset) * maxAngleOffset;
          const clampedAngle = segmentCenterAngle + clampedAngleOffset;
          const clampedAngleRad = clampedAngle * Math.PI / 180;

          xStart = currentRadius * Math.cos(clampedAngleRad);
          yStart = currentRadius * Math.sin(clampedAngleRad);
          currentAngleOffset = clampedAngleOffset;
        }

        const maxAllowedRadius = getMaxAllowedRadius(currentAngleOffset, availableAngle, maxRadius, minRadius);
        currentRadius = Math.sqrt(xStart * xStart + yStart * yStart);

        if (currentRadius > maxAllowedRadius) {
          const scale = maxAllowedRadius / currentRadius;
          xStart = xStart * scale;
          yStart = yStart * scale;
        }

        if (currentRadius < minRadius) {
          const scale = minRadius / currentRadius;
          xStart = xStart * scale;
          yStart = yStart * scale;
        }
      }
    }

    // Final forced spiral if collisions persist after all iterations
    const MIN_DISTANCE = 50;
    let hasCollisionAfterIterations = coordinates.some((coord) => {
      if (coord.id === cluster.id) return false;
      const distance = Math.sqrt(
        Math.pow(xStart - coord.xStart, 2) + Math.pow(yStart - coord.yStart, 2)
      );
      return distance < MIN_DISTANCE;
    });

    if (hasCollisionAfterIterations) {
      let spiralAttempts = 0;
      const maxSpiralAttempts = 200;
      const angleStep = 30;
      const radiusStep = 8;

      while (hasCollisionAfterIterations && spiralAttempts < maxSpiralAttempts) {
        const angle = (spiralAttempts * angleStep * Math.PI) / 180;
        const radius = spiralAttempts * radiusStep;

        const testX = xStart + radius * Math.cos(angle);
        const testY = yStart + radius * Math.sin(angle);

        const hasCollisionAtTest = coordinates.some((coord) => {
          if (coord.id === cluster.id) return false;
          const distance = Math.sqrt(
            Math.pow(testX - coord.xStart, 2) + Math.pow(testY - coord.yStart, 2)
          );
          return distance < MIN_DISTANCE;
        });

        if (!hasCollisionAtTest) {
          if (typeRadar !== "molecula" && typeRadar !== "moleculeTwo" && typeRadar !== "fintech") {
            const testRadius = Math.sqrt(testX * testX + testY * testY);
            const testAngle = Math.atan2(testY, testX) * 180 / Math.PI;
            let testAngleOffset = testAngle - segmentCenterAngle;

            while (testAngleOffset > 180) testAngleOffset -= 360;
            while (testAngleOffset < -180) testAngleOffset += 360;

            const maxAngleOffset = availableAngle / 2 * 0.90;
            const maxAllowedRadius = getMaxAllowedRadius(testAngleOffset, availableAngle, maxRadius, minRadius);

            if (Math.abs(testAngleOffset) <= maxAngleOffset &&
                testRadius <= maxAllowedRadius &&
                testRadius >= minRadius) {
              xStart = testX;
              yStart = testY;
              hasCollisionAfterIterations = false;
            }
          } else {
            xStart = testX;
            yStart = testY;
            hasCollisionAfterIterations = false;
          }
        }

        spiralAttempts++;
      }

      if (hasCollisionAfterIterations) {
        console.warn(`Не удалось устранить коллизию для точки ${cluster.id} после ${maxSpiralAttempts} попыток`);
      }
    }

    updateCoordinates(cluster.id, xStart, yStart, xEnd, yEnd);
  } else {
    xStart = existingCoord.xStart;
    yStart = existingCoord.yStart;
  }

  return {
    x,
    y,
    xStart,
    yStart,
    xEnd,
    yEnd,
    xMoleculaTwo,
    yMoleculaTwo,
    rayAngle,
  };
};

export default calculateClusterCoordinates;
